let branch='';
let sessionEpoch=0;
export const setApiBranch=(id:string)=>{branch=id};
export const bumpSessionEpoch=()=>{sessionEpoch+=1};

/*
 * Actor guard. All tabs share one session cookie, so a request sent from this tab is executed as whoever signed
 * in last in ANY tab. The app tells the client which actor (`tenantId:userId`) this tab's screens belong to;
 * when another tab reports a different actor the guard is tripped synchronously. While tripped, nothing but the
 * /auth/* endpoints is sent at all, and a response to a request started under a previous actor is never handed
 * back to the caller (so a mutation's onSuccess or a list result cannot apply under the new identity).
 */
let expectedActor='';
let actorChanged=false;
let actorGeneration=0;

/**
 * Raised instead of sending, or instead of returning a response, when the signed-in actor changed. A request
 * refused before fetch was never sent; a response dropped after the change may belong to a request the server
 * already executed, so the message never claims that it was discarded.
 */
export class SessionChangedError extends Error{
 constructor(message='Signed-in user changed in another tab. This request was not sent or its result was not applied; check the document list before entering it again.'){
  super(message);
  this.name='SessionChangedError';
 }
}

/** Machine-readable code the API returns (HTTP 409) when the X-Esaas-Actor header does not match the session. */
export const ACTOR_MISMATCH='ACTOR_MISMATCH';
/** Window event raised when the server refused a request because the session belongs to another actor. */
export const ACTOR_MISMATCH_EVENT='esaas-actor-mismatch';

/** The server refused the request (ACTOR_MISMATCH) before running it: nothing from it was saved. */
export class ActorMismatchError extends SessionChangedError{
 readonly code=ACTOR_MISMATCH;
 constructor(){
  super('Signed-in user changed in another tab. The server rejected this request and it was NOT saved.');
  this.name='ActorMismatchError';
 }
}

/** Sets the actor this tab works as (`tenantId:userId`, or '' when signed out) and clears a tripped guard. */
export const setApiActor=(actor:string)=>{
 if(actor!==expectedActor)actorGeneration+=1;
 expectedActor=actor;
 actorChanged=false;
};
/** Trips the guard: every pending response is discarded and new non-auth requests are refused before fetch. */
export const markActorChanged=()=>{actorChanged=true;actorGeneration+=1};
export const isActorChanged=()=>actorChanged;

export class ApiError extends Error{
 status:number;
 /** Structured error code from the API body (e.g. ACTOR_HEADER_INVALID), when present. */
 code?:string;
 constructor(message:string,status:number,code?:string){
  super(message);
  this.name='ApiError';
  this.status=status;
  if(code)this.code=code;
 }
}

const errorCode=(body:unknown)=>typeof body==='object'&&body!==null&&'code'in body&&typeof (body as {code:unknown}).code==='string'?(body as {code:string}).code:undefined;

export async function api<T>(path:string,options:RequestInit={}):Promise<T>{
 const authPath=path.startsWith('/auth/');
 if(actorChanged&&!authPath)throw new SessionChangedError();
 const epoch=sessionEpoch;
 const generation=actorGeneration;
 const res=await fetch('/api/v1'+path,{...options,credentials:'same-origin',headers:{
  'Content-Type':'application/json',
  ...(branch&&!authPath?{'X-Branch-Id':branch}:{}),
  ...(expectedActor?{'X-Esaas-Actor':expectedActor}:{}),
  ...options.headers,
 }});
 const raw=await res.text();
 let body:unknown=null;
 if(raw){try{body=JSON.parse(raw)}catch{body=null}}
 const code=res.ok?undefined:errorCode(body);
 if(code===ACTOR_MISMATCH){
  // The server refused to run the request because the cookie now belongs to someone else. Checked before the
  // generation test so the caller and the notice can say for certain that this request was NOT saved.
  // Never retried: the guard stays tripped until the user continues as the current user.
  markActorChanged();
  window.dispatchEvent(new Event(ACTOR_MISMATCH_EVENT));
  throw new ActorMismatchError();
 }
 if(generation!==actorGeneration)throw new SessionChangedError();
 if(!res.ok){
  if(res.status===401&&!authPath&&epoch===sessionEpoch)window.dispatchEvent(new Event('esaas-session-expired'));
  const message=typeof body==='object'&&body!==null&&'message'in body?String((body as {message:unknown}).message):'Request failed';
  throw new ApiError(message,res.status,code);
 }
 if(path==='/auth/me'&&expectedActor&&typeof body==='object'&&body!==null){
  const me=body as {tenantId?:unknown;id?:unknown};
  // The cookie now belongs to someone else (a sign-in this tab was not told about): stop sending immediately.
  if(`${String(me.tenantId)}:${String(me.id)}`!==expectedActor)markActorChanged();
 }
 return body as T;
}
